import {
  Archive,
  Download,
  LayoutGrid,
  Layers,
  List as ListIcon,
  Plus,
  RotateCcw,
  Search,
  Table as TableIcon,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ViewModeToggle, type ViewModeOption } from '@/components/ui/view-mode-toggle';
import { exportCsv, type ApiError } from '@/core/api';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { TagFilterSelect } from '@/features/tags';
import { useCompanies } from '@/features/crm/companies/queries';
import { PurgeArchivedButton } from '@/features/crm/components/PurgeArchivedButton';
import { BASE as CONTACTS_BASE } from '../api';
import { useArchiveContact, useContacts, usePurgeContact, useRestoreContact } from '../queries';
import { useContactsUi, type ContactsViewMode } from '../store/viewPrefs';
import { contactName, type Contact } from '../types';

/** Columns the backend lets us order by — keeps typos out of the URL. */
type ContactOrderingColumn =
  | 'first_name'
  | 'last_name'
  | 'email'
  | 'created'
  | 'modified';

const VIEW_MODES: readonly ViewModeOption<ContactsViewMode>[] = [
  { value: 'table', label: 'Table', icon: TableIcon },
  { value: 'list', label: 'List', icon: ListIcon },
  { value: 'cards', label: 'Cards', icon: LayoutGrid },
  { value: 'grouped', label: 'Grouped by company', icon: Layers },
];

export function ContactsListPage() {
  const navigate = useNavigate();
  const view = useContactsUi((s) => s.view);
  const setView = useContactsUi((s) => s.setView);
  const showArchived = useContactsUi((s) => s.showArchived);
  const setShowArchived = useContactsUi((s) => s.setShowArchived);
  const ordering = useContactsUi((s) => s.ordering);
  const setOrdering = useContactsUi((s) => s.setOrdering);
  const tag = useContactsUi((s) => s.tag);
  const setTag = useContactsUi((s) => s.setTag);

  // Search stays transient (cleared on reload).
  const [searchInput, setSearchInput] = useState('');
  // Live, debounced search — fires 300ms after typing stops (no Search button).
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [page, setPage] = useState(1);

  const { data, isPending, isError, error, isFetching } = useContacts({
    page,
    search: search || undefined,
    archived: showArchived ? true : undefined,
    ordering,
    tag: tag === 'all' ? undefined : Number(tag),
  });

  const archive = useArchiveContact();
  const restore = useRestoreContact();
  const purge = usePurgeContact();

  const results = data?.results ?? [];
  const onOpen = (c: Contact) => navigate(`/crm/contacts/${c.public_id}`);
  const onArchive = (c: Contact) => archive.mutate(c.id);
  const onRestore = (c: Contact) => restore.mutate(c.id);
  const onPurge = (c: Contact) => purge.mutate(c.id);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Contacts</h1>
        <Button onClick={() => navigate('/crm/contacts/new')}>
          <Plus /> New contact
        </Button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative max-w-sm flex-1">
            <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
            <Input
              value={searchInput}
              onChange={(e) => {
                setPage(1);
                setSearchInput(e.target.value);
              }}
              placeholder="Search by name or email…"
              className="pl-8"
              aria-label="Search contacts"
            />
          </div>
          <TagFilterSelect
            value={tag}
            onChange={(v) => {
              setPage(1);
              setTag(v);
            }}
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="icon"
            variant={showArchived ? 'default' : 'outline'}
            onClick={() => {
              setPage(1);
              setShowArchived(!showArchived);
            }}
            aria-pressed={showArchived}
            aria-label={showArchived ? 'Showing archived contacts' : 'Show archived contacts'}
            title={showArchived ? 'Showing archived contacts' : 'Show archived contacts'}
          >
            <Archive className="size-4" />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => {
              // Mirror the live list filters and add `export=csv`; the backend
              // returns `Content-Disposition: attachment`, so this navigation
              // downloads `contacts.csv` without leaving the SPA.
              void exportCsv(CONTACTS_BASE, {
                search: search || undefined,
                archived: showArchived ? true : undefined,
                ordering,
                tag: tag === 'all' ? undefined : Number(tag),
                export: 'csv',
              });
            }}
            aria-label="Export contacts as CSV"
            title="Export contacts as CSV"
          >
            <Download className="size-4" />
          </Button>
          <ViewModeToggle value={view} onChange={setView} modes={VIEW_MODES} />
        </div>
      </div>

      {isError ? (
        <p className="text-destructive text-sm">
          {(error as unknown as ApiError).message}
        </p>
      ) : isPending ? (
        <ContactsSkeleton />
      ) : results.length === 0 ? (
        // Empty state is consistent across views: render the table chrome with
        // an inline "No contacts found." row, so the page never collapses into a
        // tiny floating message regardless of the persisted view mode.
        <ContactsTable
          contacts={[]}
          onRowClick={() => undefined}
          emptyMessage={showArchived ? 'No archived contacts.' : undefined}
          onArchive={onArchive}
          onRestore={onRestore}
          onPurge={onPurge}
          ordering={ordering}
          onOrderingChange={setOrdering}
        />
      ) : view === 'table' ? (
        <ContactsTable
          contacts={results}
          onRowClick={onOpen}
          onArchive={onArchive}
          onRestore={onRestore}
          onPurge={onPurge}
          ordering={ordering}
          onOrderingChange={setOrdering}
        />
      ) : view === 'list' ? (
        <ContactsList contacts={results} onOpen={onOpen} />
      ) : view === 'cards' ? (
        <ContactsCards contacts={results} onOpen={onOpen} />
      ) : (
        <ContactsGroupedByCompany contacts={results} onOpen={onOpen} />
      )}

      {data && data.count > 0 ? (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            {data.count} {data.count === 1 ? 'contact' : 'contacts'}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!data.previous || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="text-sm">Page {page}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={!data.next || isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---- View renderers --------------------------------------------------------

interface ViewProps {
  contacts: Contact[];
  onOpen: (c: Contact) => void;
}

interface ContactsTableProps {
  contacts: Contact[];
  onRowClick: (c: Contact) => void;
  emptyMessage?: string;
  onArchive: (c: Contact) => void;
  onRestore: (c: Contact) => void;
  onPurge: (c: Contact) => void;
  ordering: string | undefined;
  onOrderingChange: (next: string | undefined) => void;
}

function ContactsTable({
  contacts,
  onRowClick,
  emptyMessage,
  onArchive,
  onRestore,
  onPurge,
  ordering,
  onOrderingChange,
}: ContactsTableProps) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            {/* The Name cell renders a combined first/last label, so we sort
                by last_name — that's the most useful default for a roster. */}
            <SortableTableHead<ContactOrderingColumn>
              column="last_name"
              value={ordering}
              onChange={onOrderingChange}
            >
              Name
            </SortableTableHead>
            <SortableTableHead<ContactOrderingColumn>
              column="email"
              value={ordering}
              onChange={onOrderingChange}
              className="hidden sm:table-cell"
            >
              Email
            </SortableTableHead>
            <TableHead className="hidden md:table-cell">Title</TableHead>
            <TableHead className="hidden lg:table-cell">Phone</TableHead>
            <TableHead className="w-16 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {contacts.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-muted-foreground py-10 text-center">
                {emptyMessage ?? 'No contacts found.'}
              </TableCell>
            </TableRow>
          ) : (
            contacts.map((c) => {
              const label = contactName(c);
              return (
                <TableRow
                  key={c.id}
                  className="cursor-pointer"
                  onClick={() => onRowClick(c)}
                >
                  <TableCell className="font-medium">{label}</TableCell>
                  <TableCell className="text-muted-foreground hidden sm:table-cell">
                    {c.email || '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground hidden md:table-cell">
                    {c.title || '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground hidden lg:table-cell">
                    {c.phone_number || '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    {c.is_archived ? (
                      <span className="inline-flex items-center justify-end">
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label={`Restore ${label}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onRestore(c);
                          }}
                        >
                          <RotateCcw className="size-4" />
                        </Button>
                        <PurgeArchivedButton name={label} onPurge={() => onPurge(c)} />
                      </span>
                    ) : (
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label={`Archive ${label}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onArchive(c);
                        }}
                      >
                        <Archive className="size-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function ContactsList({ contacts, onOpen }: ViewProps) {
  return (
    <ul className="divide-y rounded-md border">
      {contacts.map((c) => (
        <li key={c.id}>
          {/* Use a real <button> so the row is interactive a11y-correctly. */}
          <button
            type="button"
            onClick={() => onOpen(c)}
            className="hover:bg-accent/30 flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left"
          >
            <span className="min-w-0 flex-1 truncate text-sm font-medium">
              {contactName(c)}
            </span>
            <span className="text-muted-foreground hidden text-xs sm:inline">
              {c.title || '—'}
            </span>
            <span className="text-muted-foreground hidden text-xs md:inline">
              {c.email || '—'}
            </span>
            <span className="text-muted-foreground hidden text-xs lg:inline">
              {c.phone_number || '—'}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function ContactsCards({ contacts, onOpen }: ViewProps) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {contacts.map((c) => (
        <Card
          key={c.id}
          role="button"
          tabIndex={0}
          onClick={() => onOpen(c)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpen(c);
            }
          }}
          className="hover:bg-accent/30 cursor-pointer transition-colors"
        >
          <CardHeader>
            <CardTitle className="text-base">{contactName(c)}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-xs">
            <p className="text-muted-foreground">{c.email || '—'}</p>
            <p className="text-muted-foreground">{c.phone_number || '—'}</p>
            <p className="text-muted-foreground">{c.title || '—'}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ContactsGroupedByCompany({ contacts, onOpen }: ViewProps) {
  // Pull a page of companies to resolve labels; the grouping itself is purely
  // client-side bucketing of the contacts already on screen.
  const { data: companiesPage } = useCompanies({ page: 1 });
  const companyNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const co of companiesPage?.results ?? []) map.set(co.id, co.name);
    return map;
  }, [companiesPage]);

  const groups = useMemo(() => {
    const byCompany = new Map<number | null, Contact[]>();
    for (const c of contacts) {
      const key = c.company ?? null;
      const list = byCompany.get(key) ?? [];
      list.push(c);
      byCompany.set(key, list);
    }
    const resolved = Array.from(byCompany.entries()).map(([id, list]) => ({
      id,
      label:
        id === null
          ? 'No company'
          : (companyNameById.get(id) ?? `Company #${id}`),
      contacts: list,
    }));
    // Companies first (alpha by name), "No company" bucket last.
    resolved.sort((a, b) => {
      if (a.id === null) return 1;
      if (b.id === null) return -1;
      return a.label.localeCompare(b.label);
    });
    return resolved;
  }, [contacts, companyNameById]);

  return (
    <div className="space-y-6">
      {groups.map((group) => (
        <section
          key={group.id === null ? 'no-company' : `company-${group.id}`}
          aria-labelledby={`group-${group.id === null ? 'none' : group.id}`}
        >
          <h2
            id={`group-${group.id === null ? 'none' : group.id}`}
            className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase"
          >
            {group.label}
            <Badge variant="outline" className="text-[10px]">
              {group.contacts.length}
            </Badge>
          </h2>
          <ContactsCards contacts={group.contacts} onOpen={onOpen} />
        </section>
      ))}
    </div>
  );
}

function ContactsSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}
